import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import ReactMarkdown from 'react-markdown';
import ProductCard from './ProductCard';
import HomeAnimatedInvitations from './HomeAnimatedInvitations';
import { Helmet } from 'react-helmet-async';
import HomeLifeEvents from './HomeLifeEvents';
import { faqJsonLd } from './Faq';
import { useFaqDe } from '../utils/faq';
import { resolveMediaUrl } from '../utils/media';
import { markdownToExcerpt } from '../utils/text';
import { useT } from '../i18n';
import { useProductTexts, usePageTexts } from '../utils/productI18n';

gsap.registerPlugin(ScrollTrigger, useGSAP);

/* ── Styles ─────────────────────────────────────────────────── */
const heroStyle = {
  background: 'linear-gradient(135deg, #fdf0ee 0%, #ece8f7 52%, #fdf4ec 100%)',
  borderRadius: '28px',
  padding: '56px 32px 52px',
  marginBottom: '52px',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  border: '1px solid rgba(232,213,209,0.4)',
};

const featureCardStyle = {
  background: '#fefcfa',
  borderRadius: '20px',
  padding: '32px 24px',
  textAlign: 'center',
  border: '1px solid rgba(232,213,209,0.5)',
  boxShadow: '0 4px 20px rgba(196,143,139,0.08)',
  height: '100%',
  transition: 'transform 0.28s ease, box-shadow 0.28s ease',
};

const trustBadgeStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 22px',
  background: 'rgba(255,255,255,0.82)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  borderRadius: '50px',
  border: '1px solid rgba(232,213,209,0.55)',
  boxShadow: '0 2px 12px rgba(196,143,139,0.10)',
};

const testimonialStyle = {
  background: '#fefcfa',
  borderRadius: '20px',
  padding: '28px',
  border: '1px solid rgba(232,213,209,0.5)',
  boxShadow: '0 4px 18px rgba(196,143,139,0.08)',
  height: '100%',
};

const faqItemStyle = {
  background: '#fefcfa',
  borderRadius: '16px',
  padding: '24px 28px',
  marginBottom: '12px',
  border: '1px solid rgba(232,213,209,0.45)',
  boxShadow: '0 2px 10px rgba(196,143,139,0.06)',
};

/* ── Component ──────────────────────────────────────────────── */
const ProductList = () => {
  const t = useT('home');
  const faqAccueil = useFaqDe('accueil'); // questions de l'accueil dans la langue courante
  const tp = useProductTexts(); // titre, description courte et étiquette traduits (repli : français)
  const pageHome = usePageTexts('home'); // texte de la page « home » en base, traduit s'il existe
  const [products, setProducts] = useState([]);
  const [pageContent, setPageContent] = useState('');
  const [featuredImage, setFeaturedImage] = useState(null);
  const VAT_RATE = 0.2;
  const containerRef = useRef(null);
  const heroVideoRef = useRef(null);

  // Déclenche explicitement la lecture (l'attribut autoPlay seul échoue parfois : la carte
  // vidéo démarre cachée le temps de l'animation de révélation au scroll, et certains
  // navigateurs n'autoplay jamais un <video> qui était masqué à l'instant du premier essai).
  // muted + appel JS explicite reste autorisé par les navigateurs indépendamment de ça.
  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return;
    const tryPlay = () => { video.play().catch(() => {}); };
    tryPlay();
    // Retente une fois la carte effectivement visible (fin de l'animation de révélation).
    const t = setTimeout(tryPlay, 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    axios.get('https://joli-mot.fr/api-2026.php', {
      params: { cleApi: '34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75', service: 'getProducts' }
    })
      .then(response => setProducts(response.data.data))
      .catch(error => console.error('There was an error fetching the products!', error));
  }, []);

  useEffect(() => {
    axios.get('https://joli-mot.fr/api-2026.php', {
      params: {
        cleApi: '34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75',
        service: 'getPageByUrl',
        url: 'home',
      }
    })
      .then(response => {
        // La page "home" n'a pas forcément de contenu en base (getPageByUrl peut renvoyer
        // data: null) : ne pas planter, le reste de l'accueil (hero, produits) ne dépend pas
        // de ce contenu optionnel.
        const contentData = response.data?.data;
        if (contentData) {
          setPageContent(contentData.contenu || '');
          if (contentData.featured_image) setFeaturedImage(contentData.featured_image);
        }
      })
      .catch(error => console.error('There was an error fetching the page content!', error));
  }, []);

  /* ── Hero entrance ──────────────────────────────────────────
     Animation CSS pure (voir @keyframes heroFadeUp dans App.css), pas GSAP :
     le timeline GSAP précédent restait bloqué à opacity:0 sur cette section
     de façon intermittente (StrictMode / ordre de montage), rendant le
     sous-titre et le CTA invisibles. Une animation CSS déclenchée par le
     navigateur au montage ne peut pas rester "coincée" de cette façon. */

  /* ── Scroll-reveal animations (reruns when products load) ─── */
  useGSAP(() => {
    // Products title
    gsap.set('.products-title', { autoAlpha: 0, y: 24 });
    gsap.to('.products-title', {
      autoAlpha: 1, y: 0, duration: 0.6, ease: 'power2.out',
      scrollTrigger: { trigger: '.products-title', start: 'top 86%', once: true },
    });

    // Product + demo cards
    gsap.set('.product-item', { autoAlpha: 0, y: 50 });
    ScrollTrigger.batch('.product-item', {
      onEnter: (batch) => gsap.to(batch, { autoAlpha: 1, y: 0, stagger: 0.1, duration: 0.65, ease: 'power2.out', overwrite: true }),
      start: 'top 89%',
      once: true,
    });

    // Features section
    gsap.set('.features-title', { autoAlpha: 0, y: 24 });
    gsap.to('.features-title', {
      autoAlpha: 1, y: 0, duration: 0.6, ease: 'power2.out',
      scrollTrigger: { trigger: '.features-title', start: 'top 86%', once: true },
    });

    gsap.set('.feature-item', { autoAlpha: 0, y: 32 });
    ScrollTrigger.batch('.feature-item', {
      onEnter: (batch) => gsap.to(batch, { autoAlpha: 1, y: 0, stagger: 0.1, duration: 0.55, ease: 'power2.out', overwrite: true }),
      start: 'top 89%',
      once: true,
    });

    // Testimonials
    gsap.set('.testimonials-title', { autoAlpha: 0, y: 20 });
    gsap.to('.testimonials-title', {
      autoAlpha: 1, y: 0, duration: 0.6, ease: 'power2.out',
      scrollTrigger: { trigger: '.testimonials-title', start: 'top 86%', once: true },
    });

    gsap.set('.testimonial-item', { autoAlpha: 0, y: 32 });
    ScrollTrigger.batch('.testimonial-item', {
      onEnter: (batch) => gsap.to(batch, { autoAlpha: 1, y: 0, stagger: 0.12, duration: 0.6, ease: 'power2.out', overwrite: true }),
      start: 'top 89%',
      once: true,
    });

    // FAQ
    gsap.set('.faq-title', { autoAlpha: 0, y: 20 });
    gsap.to('.faq-title', {
      autoAlpha: 1, y: 0, duration: 0.55, ease: 'power2.out',
      scrollTrigger: { trigger: '.faq-title', start: 'top 86%', once: true },
    });

    gsap.set('.faq-item', { autoAlpha: 0, x: -24 });
    ScrollTrigger.batch('.faq-item', {
      onEnter: (batch) => gsap.to(batch, { autoAlpha: 1, x: 0, stagger: 0.09, duration: 0.5, ease: 'power2.out', overwrite: true }),
      start: 'top 89%',
      once: true,
    });

    ScrollTrigger.refresh();
  }, { scope: containerRef, dependencies: [products], revertOnUpdate: true });

  return (
    <Box ref={containerRef} className="boxAllProds" sx={{ padding: { xs: 1, md: 3 }, position: 'relative' }}>

      {/* ── Hero ────────────────────────────────────────────── */}
      <Box sx={heroStyle}>
        <Typography
          variant="h1"
          component="h1"
          sx={{ marginBottom: 2.5, lineHeight: 1.15, animation: 'heroFadeUp 0.7s ease both' }}
        >
          {t('home.hero.title')}
        </Typography>
        <Typography
          component="p"
          sx={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: { xs: '1.15rem', md: '1.35rem' },
            fontStyle: 'italic',
            color: '#7a6060',
            maxWidth: '680px',
            margin: '0 auto 16px',
            lineHeight: 1.6,
            animation: 'heroFadeUp 0.7s ease both',
            animationDelay: '0.12s',
          }}
        >
          {t.rich('home.hero.intro', {}, {
            invitations: <Link to="/faire-part-et-invitations-animes" style={{ color: '#c48f8b', fontWeight: 600 }} />,
            cards: <Link to="/cartes-a-gratter-personnalisees" style={{ color: '#c48f8b', fontWeight: 600 }} />,
          })}
        </Typography>
        <Typography
          component="p"
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: { xs: '1rem', md: '1.1rem' },
            color: '#8C8BC4',
            fontWeight: 500,
            maxWidth: '600px',
            margin: '0 auto 32px',
            animation: 'heroFadeUp 0.7s ease both',
            animationDelay: '0.22s',
          }}
        >
          {t('home.hero.tagline')}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
          {[
            { icon: '⚡', label: t('home.hero.badgeInstant') },
            { icon: '✏️', label: t('home.hero.badgeCustomizable') },
            { icon: '📱', label: t('home.hero.badgeChannels') },
          ].map(({ icon, label }, i) => (
            <Box
              key={label}
              sx={{ ...trustBadgeStyle, animation: 'heroFadeUp 0.7s ease both', animationDelay: `${0.32 + i * 0.08}s` }}
            >
              <span style={{ fontSize: '20px' }}>{icon}</span>
              <span style={{ fontWeight: 500, color: '#3a2b2b', fontSize: '0.9rem' }}>{label}</span>
            </Box>
          ))}
        </Box>
      </Box>

      <HomeLifeEvents />

      {/* ── Titre produits ──────────────────────────────────── */}
      <Typography
        variant="h2"
        component="h2"
        className="products-title"
        sx={{ marginBottom: 4, textAlign: 'center' }}
      >
        {t('home.models.title')}
      </Typography>

      {/* ── Grille produits ─────────────────────────────────── */}
      <Grid className="boxGridProd" container spacing={3}>

        {/* Vidéo démo */}
        <Grid item xs={12} md={6} lg={4} className="product-item">
          <Card sx={{
            borderRadius: '20px',
            overflow: 'hidden',
            border: '1px solid rgba(196,143,139,0.25)',
            boxShadow: '0 4px 20px rgba(196,143,139,0.12)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#fefcfa',
          }}>
            <Box sx={{
              background: 'linear-gradient(90deg, #d3a6a1 0%, #c48f8b 100%)',
              padding: '14px 20px',
              textAlign: 'center',
            }}>
              <Typography variant="h5" sx={{ color: 'white !important', margin: 0, fontSize: '1.1rem !important' }}>
                {t('home.demo.title')}
              </Typography>
            </Box>
            <video
              ref={heroVideoRef}
              src="/video_carre2.mp4"
              poster="/assets_react/demos/demo_fleurs-aquarelles-bleues.jpg"
              controls
              autoPlay
              loop
              muted
              playsInline
              style={{ width: '100%', height: 'auto' }}
            />
            <Box sx={{ padding: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="body1" sx={{ color: '#7a6060', lineHeight: 1.7 }}>
                {t.rich('home.demo.body')}
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* Produits — on ne liste que les produits ayant au moins un visuel rattaché
            dans la bibliothèque (config_carte) : featured_image seul (repli historique)
            ne suffit pas à apparaître dans le catalogue, cf. cas "Pâques" 2026-09-16. */}
        {products.filter(product => product.visuels?.length > 0).map(product => {
          const prixTTC = parseFloat(product.prix_ttc) || 0;
          const prixHT = parseFloat(product.prix_ht) || 0;
          const priceTTC = prixTTC || (prixHT * (1 + VAT_RATE)) || 0;
          const shown = tp(product);

          return (
            <Grid item xs={12} md={6} lg={4} key={product.id} className="product-item" sx={{ display: 'flex' }}>
              <ProductCard
                visuels={product.visuels?.length ? product.visuels : (product.featured_image ? [{ url: product.featured_image }] : [])}
                title={shown.titre}
                price={priceTTC}
                badge={product.etiquette}
                badgeLabel={shown.etiquette}
                linkTo={`/produit/${product.url}`}
                editPath={`/gestion-catalogue?editId=${product.idProd}`}
              >
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, lineHeight: 1.65 }}>
                  {markdownToExcerpt(shown.description_courte || shown.description, 100)}
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    component={Link}
                    to={`/produit/${product.url}`}
                    sx={{ borderRadius: '50px', padding: '8px 22px', fontWeight: 500 }}
                  >
                    {t('home.models.details')}
                  </Button>
                </Box>
              </ProductCard>
            </Grid>
          );
        })}
      </Grid>

      {/* ── Faire-part et invitations animés ─────────────────── */}
      <HomeAnimatedInvitations />

      {/* ── Avantages ───────────────────────────────────────── */}
      <Box sx={{ mt: 8, mb: 5 }}>
        <Typography variant="h2" component="h2" className="features-title" sx={{ textAlign: 'center', mb: 4 }}>
          {t('home.features.title')}
        </Typography>

        <Grid container spacing={3}>
          {[
            { key: 'surprise', icon: '🎁' },
            { key: 'eco', icon: '🌿' },
            { key: 'cheap', icon: '💸' },
            { key: 'instant', icon: '⚡' },
          ].map(({ key, icon }) => (
            <Grid item xs={12} sm={6} md={3} key={key} className="feature-item">
              <Box sx={featureCardStyle} className="feature-card">
                <Box sx={{ fontSize: '44px', mb: 2, lineHeight: 1 }}>{icon}</Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    fontWeight: 600,
                    fontSize: '1.2rem !important',
                    mb: 1,
                    color: '#3a2b2b',
                  }}
                >
                  {t(`home.features.${key}.title`)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                  {t(`home.features.${key}.body`)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Témoignages ─────────────────────────────────────── */}
      <Box sx={{
        mt: 6,
        mb: 4,
        background: 'linear-gradient(135deg, #f5f0ff 0%, #fff5f5 100%)',
        borderRadius: '28px',
        padding: { xs: 3, md: 5 },
        textAlign: 'center',
        border: '1px solid rgba(232,213,209,0.35)',
      }}>
        <Typography variant="h2" component="h2" className="testimonials-title" sx={{ mb: 4 }}>
          {t('home.testimonials.title')}
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          {[
            { quote: t('home.testimonials.pregnancy'), author: 'Marie L.' },
            { quote: t('home.testimonials.whatsapp'), author: 'Sophie D.' },
            { quote: t('home.testimonials.surprise'), author: 'Julie M.' },
          ].map(({ quote, author }) => (
            <Grid item xs={12} md={4} key={author} className="testimonial-item">
              <Box sx={testimonialStyle}>
                <Typography sx={{ fontSize: '1.1rem', mb: 1.5, color: '#c48f8b', letterSpacing: '0.05em' }}>
                  ★★★★★
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    fontStyle: 'italic',
                    fontSize: '1.1rem',
                    color: '#3a2b2b',
                    lineHeight: 1.7,
                    mb: 2,
                  }}
                >
                  {quote}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#c48f8b' }}>
                  {author}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── FAQ ─────────────────────────────────────────────── */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(faqJsonLd(faqAccueil))}</script>
        </Helmet>
        <Typography variant="h2" component="h2" className="faq-title" sx={{ textAlign: 'center', mb: 3 }}>
          {t('home.faq.title')}
        </Typography>
        <Box sx={{ maxWidth: '800px', margin: '0 auto' }}>
          {faqAccueil.map(({ q, a }) => (
            <Box key={q} className="faq-item" sx={faqItemStyle}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontWeight: 600,
                  fontSize: '1.2rem !important',
                  color: '#3a2b2b',
                  mb: 1,
                }}
              >
                {q}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {a}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to="/carte-a-gratter-digitale-questions-frequentes"
            sx={{ borderRadius: '50px', padding: '12px 36px', fontWeight: 500, fontSize: '1rem' }}
          >
            {t('home.faq.all')}
          </Button>
        </Box>
      </Box>

      {/* ── Contenu SEO CMS ─────────────────────────────────── */}
      {/* Rien en base pour la page « home » = pas de bloc (sinon un cadre vide s'affiche en bas). */}
      {String((pageHome && pageHome.contenu) || pageContent || '').trim() && (
      <Box sx={{ mt: 5 }} className="contentSeo">
        <ReactMarkdown
          components={{
            img: ({ node, src, alt, ...props }) => (
              <img src={resolveMediaUrl(src)} alt={alt || ''} {...props} />
            ),
          }}
        >
          {(pageHome && pageHome.contenu) || pageContent}
        </ReactMarkdown>
      </Box>
      )}

      {featuredImage && (
        <img
          src={resolveMediaUrl(featuredImage)}
          alt={t('home.featuredImageAlt')}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '150px',
            height: '150px',
            objectFit: 'cover',
            borderRadius: '50%',
            margin: '10px',
          }}
        />
      )}
    </Box>
  );
};

export default ProductList;
